import type { TOC } from '@ember/component/template-only';
import { Popover } from 'tio-ui/components/popover';
import Component from '@glimmer/component';
import ProgressTrackerItems from './progress-tracker-items.gts';
import { PhCheck, PhWarning } from 'ember-phosphor-icons';

export interface ProgressTrackerSignature {
  Args: {
    primaryColor?: string;
    secondaryColor?: string;
    actionColor?: string;
    steps: Step[];
  };
  Element: HTMLElement;
}

export type Step = {
  title?: string;
  completed?: boolean;
  inProgress?: boolean;
  details?: Detail[];
  actionNeeded?: boolean;
  iconBackground?: string;
};

export interface Detail {
  componentName?: string;
  props?: Record<string, unknown>;
}

interface S {
  step: Step;
  index: number;
  details?: Detail[];
}

const titleClass = `mt-2 text-center text-gray-800 dark:text-gray-200 underline underline-offset-2 cursor-pointer`;

const isEmptyComponent = (component: object) => {
  return Object.keys(component).length === 0;
};

const hasDetails = (step: Step) => {
  return step.details && step.details.length > 0;
};

const Details: TOC<S> = <template>
  <Popover as |pop|>
    <button
      class={{titleClass}}
      data-test-progress-trigger
      type="button"
      {{pop.trigger}}
      {{pop.anchor}}
    >{{@step.title}}</button>
    {{#if (hasDetails @step)}}
      <pop.Content
        @class="p-2 w-96 shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600"
        @preventFocusRestore={{true}}
        data-test-progress-details
      >
        <p
          class="text-sm font-semibold uppercase text-gray-900 dark:text-gray-100"
        >{{@step.title}}</p>
        <div>
          {{#each @step.details as |detail|}}
            <ProgressTrackerItems @componentName={{detail.componentName}} @props={{detail.props}} />
          {{/each}}
        </div>
      </pop.Content>
    {{/if}}
  </Popover>
</template>;

export default class ProgressTracker extends Component<ProgressTrackerSignature> {
  get primaryColor() {
    return this.args.primaryColor ? this.args.primaryColor : 'violet-800';
  }
  get secondaryColor() {
    return this.args.secondaryColor ? this.args.secondaryColor : 'gray-50';
  }
  get actionColor() {
    return this.args.actionColor ? this.args.actionColor : 'yellow-500';
  }
  get nonEmptySteps() {
    return this.args.steps.filter((step) => !isEmptyComponent(step));
  }

  isLastStep(index: number, steps: Step[]) {
    return index === steps.length - 1;
  }

  <template>
    <div ...attributes>
      <ul class="isolate flex">
        {{#each this.nonEmptySteps as |step index|}}
          <li class="relative flex-1 flex flex-col items-center mx-2">
            {{#if step.completed}}
              <div
                class="flex items-center justify-center w-10 h-10 bg-{{this.primaryColor}}
                  rounded-full z-10"
                data-progress-bubble={{index}}
              >
                <PhCheck @size="20" class="text-white" data-progress-check />
              </div>
              <Details @step={{step}} @index={{index}} />
              {{#unless (this.isLastStep index this.nonEmptySteps)}}
                <div
                  class="absolute top-1/4 lg:top-1/3 transform -translate-y-1/2 left-1/2 w-full h-0.5 bg-{{this.primaryColor}}"
                >
                </div>
              {{/unless}}
            {{else if step.actionNeeded}}
              <div
                class="flex items-center justify-center w-10 h-10 bg-{{this.actionColor}}
                  rounded-full z-10"
                data-progress-bubble={{index}}
              >
                <PhWarning @size="20" class="text-white" data-progress-check />
              </div>
              <Details @step={{step}} @index={{index}} />
              {{#unless (this.isLastStep index this.nonEmptySteps)}}
                <div
                  class="absolute top-1/4 lg:top-1/3 transform -translate-y-1/2 left-1/2 w-full h-0.5 bg-gray-300 dark:bg-gray-600"
                ></div>
              {{/unless}}
            {{else if step.inProgress}}
              <div
                class="flex items-center justify-center w-10 h-10 bg-{{this.secondaryColor}}
                  dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 border-{{this.primaryColor}}
                  rounded-full z-10"
                data-progress-bubble={{index}}
              >
                <div class="h-3 w-3 rounded-full bg-{{this.primaryColor}}"></div>
              </div>
              <Details @step={{step}} @index={{index}} />
              {{#unless (this.isLastStep index this.nonEmptySteps)}}
                <div
                  class="absolute top-1/4 lg:top-1/3 transform -translate-y-1/2 left-1/2 w-full h-0.5 bg-gray-300 dark:bg-gray-600"
                ></div>
              {{/unless}}
              {{! Control flow for if a step is not necessary via program template }}
            {{else if (isEmptyComponent step)}}{{else}}
              <div
                class="flex items-center justify-center w-10 h-10 bg-white dark:bg-gray-700 border-8 border-gray-200 dark:border-gray-600 rounded-full z-10"
                data-progress-bubble={{index}}
              >
              </div>
              <Details @step={{step}} @index={{index}} />
              {{#unless (this.isLastStep index this.nonEmptySteps)}}
                <div
                  class="absolute top-1/4 lg:top-1/3 transform -translate-y-1/2 left-1/2 w-full h-0.5 bg-gray-300 dark:bg-gray-600"
                ></div>
              {{/unless}}
            {{/if}}
          </li>
        {{/each}}
      </ul>
    </div>
  </template>
}
